// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DetailPageBody } from '@/components/detail-page-body';
import { DetailPageHeader } from '@/components/detail-page-header';
import { DetailPageHeaderField } from '@/components/detail-page-header-field';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { HorizontalNavigationLink } from '@/components/horizontal-navigation-link';
import { Link } from '@/components/link';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TransportRefLabel } from '@/components/transport-ref-label';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { LocationIntegrationStatus } from '@/location/components/location-integration-status';
import {
  getEffectiveAddress,
  getEffectiveLocationName,
  isEffectiveThirdPartyEnabled,
} from '@/location/utilities/get-effective-third-party';
import { PurchaseOrderAttachmentsSection } from '@/purchase-order/components/purchase-order-attachments-section';
import { PurchaseOrderAuditCard } from '@/purchase-order/components/purchase-order-audit-card';
import { PurchaseOrderCancelDialog } from '@/purchase-order/components/purchase-order-cancel-dialog';
import { PurchaseOrderDetailsSkeleton } from '@/purchase-order/components/purchase-order-details-skeleton';
import { PurchaseOrderDialog } from '@/purchase-order/components/purchase-order-dialog';
import { PurchaseOrderFormData } from '@/purchase-order/components/purchase-order-form';
import { PurchaseOrderHeaderActions } from '@/purchase-order/components/purchase-order-header-actions';
import { PurchaseOrderNotesControl } from '@/purchase-order/components/purchase-order-notes-control';
import { PurchaseOrderPhaseIndicator } from '@/purchase-order/components/purchase-order-phase-indicator';
import { PurchaseOrderSendTo3PLDialog } from '@/purchase-order/components/purchase-order-send-to-3pl-dialog';
import { usePurchaseOrder } from '@/purchase-order/hooks/use-purchase-order';
import {
  purchaseOrderDetailQueryKey,
  purchaseOrdersQueryKey,
} from '@/purchase-order/query-keys';
import { InboundShipmentDialog } from '@/shipment/components/inbound-shipment-dialog';
import { extractSparseItem } from '@/sparse/utilities/to-sparse-item';
import {
  Box,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import { Suspense, useMemo, useState } from 'react';

export const Route = createFileRoute('/_app/purchase-orders/$purchaseOrderKey')(
  {
    component: PurchaseOrderRoute,
  }
);

function PurchaseOrderRoute() {
  const { purchaseOrderKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="purchase order"
      listPath="/purchase-orders"
      listLabel="Go to purchase orders"
    >
      <Suspense fallback={<PurchaseOrderDetailsSkeleton />}>
        <PurchaseOrderContent purchaseOrderKey={purchaseOrderKey} />
      </Suspense>
    </NotFoundErrorBoundary>
  );
}

function PurchaseOrderContent(props: { purchaseOrderKey: string }) {
  const { purchaseOrder } = usePurchaseOrder(props.purchaseOrderKey);
  const queryClient = useQueryClient();
  const invalidateAfterDriftPull = () => {
    void queryClient.invalidateQueries({ queryKey: purchaseOrdersQueryKey });
    void queryClient.invalidateQueries({ queryKey: ['location'] });
  };
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isReceiveDialogOpen, setIsReceiveDialogOpen] = useState(false);
  const [isSendTo3PLDialogOpen, setIsSendTo3PLDialogOpen] = useState(false);
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);

  // Build clone form data: pre-populate supplier, locations, and items
  const cloneFormData = useMemo((): PurchaseOrderFormData => {
    return {
      customer: purchaseOrder.supplier,
      externalPoNumber: null,
      fromLocation: purchaseOrder.fromLocation,
      toLocation: purchaseOrder.toLocation,
      promiseShipDate: null,
      promiseDeliveryDate: null,
      orderNotes: null,
      expectedTransportCarrier: null,
      expectedTransportReferenceNumber: null,
      expectedTransportCarrierName: null,
      items: purchaseOrder.items.map((item) => ({
        item: extractSparseItem(item),
        quantity: item.requestedQuantity,
      })),
    };
  }, [purchaseOrder]);

  // Handler for opening edit dialog
  const handleOpenEditDialog = () => {
    setIsEditDialogOpen(true);
  };

  // Handler for closing edit dialog
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
  };

  // Handler for opening cancel dialog
  const handleOpenCancelDialog = () => {
    setIsCancelDialogOpen(true);
  };

  // Handler for closing cancel dialog
  const handleCloseCancelDialog = () => {
    setIsCancelDialogOpen(false);
  };

  // Handler for opening receive dialog
  const handleOpenReceiveDialog = () => {
    setIsReceiveDialogOpen(true);
  };

  // Handler for closing receive dialog
  const handleCloseReceiveDialog = () => {
    setIsReceiveDialogOpen(false);
  };

  // Handler for opening send to 3PL dialog
  const handleOpenSendTo3PLDialog = () => {
    setIsSendTo3PLDialogOpen(true);
  };

  // Handler for closing send to 3PL dialog
  const handleCloseSendTo3PLDialog = () => {
    setIsSendTo3PLDialogOpen(false);
  };

  // Handler for opening clone dialog
  const handleOpenCloneDialog = () => {
    setIsCloneDialogOpen(true);
  };

  // Handler for closing clone dialog
  const handleCloseCloneDialog = () => {
    setIsCloneDialogOpen(false);
  };

  // Handler for successful send to 3PL
  const handleSendTo3PLSuccess = (
    updatedPurchaseOrder: typeof purchaseOrder
  ) => {
    // Update the query cache with the updated purchase order
    queryClient.setQueryData(
      purchaseOrderDetailQueryKey(props.purchaseOrderKey),
      updatedPurchaseOrder
    );
    // Invalidate all purchase order queries to ensure everything stays in sync
    queryClient.invalidateQueries({
      queryKey: purchaseOrdersQueryKey,
      refetchType: 'active',
    });
  };

  // Handler for successful cancellation
  const handleCancelSuccess = (updatedPurchaseOrder: typeof purchaseOrder) => {
    // Update the query cache with the cancelled purchase order
    queryClient.setQueryData(
      purchaseOrderDetailQueryKey(props.purchaseOrderKey),
      updatedPurchaseOrder
    );
    // Invalidate all purchase order queries to ensure everything stays in sync
    queryClient.invalidateQueries({
      queryKey: purchaseOrdersQueryKey,
      refetchType: 'active',
    });
  };

  return (
    <Container maxWidth="xl">
      <DetailPageHeader
        parentBreadcrumb={
          <Link
            to="/purchase-orders"
            variant="body2"
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            Purchase orders
          </Link>
        }
        currentBreadcrumb={purchaseOrder.purchaseOrderNumber}
        title={purchaseOrder.purchaseOrderNumber}
        actions={
          <PurchaseOrderHeaderActions
            status={purchaseOrder.status}
            isThirdPartyReceive={purchaseOrder.thirdParty.isEnabled}
            isDestinationThirdParty={isEffectiveThirdPartyEnabled(
              purchaseOrder.toLocation
            )}
            hasZeroQuantityItem={purchaseOrder.items.some(
              (item) => item.requestedQuantity === 0
            )}
            purchaseOrderKey={purchaseOrder.key}
            purchaseOrderNumber={purchaseOrder.purchaseOrderNumber}
            lastSyncedAt={purchaseOrder.thirdParty.lastSyncedAt}
            lastThirdPartyUpdateAt={purchaseOrder.thirdParty.lastUpdateAt}
            hasSyncError={purchaseOrder.thirdParty.hasSyncError}
            syncErrorMessage={purchaseOrder.thirdParty.syncErrorMessage ?? ''}
            onEdit={handleOpenEditDialog}
            onCancel={handleOpenCancelDialog}
            onReceive={handleOpenReceiveDialog}
            onSendTo3PL={handleOpenSendTo3PLDialog}
            onClone={handleOpenCloneDialog}
          />
        }
      >
        {purchaseOrder.externalPurchaseOrderNumber && (
          <DetailPageHeaderField label="External PO">
            {purchaseOrder.externalPurchaseOrderNumber}
          </DetailPageHeaderField>
        )}
        <DetailPageHeaderField label="Supplier">
          <Link
            to="/customers/$customerKey"
            params={{ customerKey: purchaseOrder.supplier.key }}
            variant="body2"
          >
            {purchaseOrder.supplier.name}
          </Link>
        </DetailPageHeaderField>
      </DetailPageHeader>

      {/* Main content */}
      <DetailPageBody
        side={
          <>
            {/* Notes Section */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <HardfinTabCardWrapper title="Notes" hideDivider>
                <Box sx={{ height: '12.4rem' }}>
                  <PurchaseOrderNotesControl
                    purchaseOrder={purchaseOrder}
                    isDestinationThirdParty={isEffectiveThirdPartyEnabled(
                      purchaseOrder.toLocation
                    )}
                  />
                </Box>
              </HardfinTabCardWrapper>
            </Box>

            {/* Attachments Section (files + links) */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PurchaseOrderAttachmentsSection
                purchaseOrderKey={props.purchaseOrderKey}
                purchaseOrderId={purchaseOrder.id}
              />
            </Box>

            {/* Audit log */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PurchaseOrderAuditCard
                purchaseOrderKey={props.purchaseOrderKey}
              />
            </Box>
          </>
        }
      >
        {/* Phase Indicator and Locations Card */}
        <Paper sx={{ padding: 3, marginBottom: 2 }} variant="outlined">
          {/* Phase Indicator */}
          <PurchaseOrderPhaseIndicator
            currentStatus={purchaseOrder.status}
            cancelledReason={purchaseOrder.cancellationReason}
            isThirdPartyEnabled={purchaseOrder.thirdParty.isEnabled}
            createdAt={purchaseOrder.createdAt}
            shippedAt={purchaseOrder.mostRecentShippedAt}
            deliveredAt={purchaseOrder.mostRecentDeliveredAt}
            receivedAt={purchaseOrder.mostRecentReceivedAt}
          />

          {/* From Location */}
          <Box marginTop={4} paddingX={1}>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="column" spacing={0.5}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="subtitle2">From: </Typography>
                  <Typography variant="body2">
                    {purchaseOrder.fromLocation
                      ? getEffectiveLocationName(purchaseOrder.fromLocation)
                      : 'Not set'}
                  </Typography>
                  <LocationIntegrationStatus
                    location={purchaseOrder.fromLocation}
                    onResolved={invalidateAfterDriftPull}
                  />
                  {isEffectiveThirdPartyEnabled(purchaseOrder.fromLocation) && (
                    <Chip label="3PL" size="small" />
                  )}
                </Stack>
                {purchaseOrder.fromLocation &&
                  getEffectiveAddress(purchaseOrder.fromLocation) && (
                    <Typography variant="body2" color="text.secondary">
                      {getEffectiveAddress(purchaseOrder.fromLocation)}
                    </Typography>
                  )}
              </Stack>
              <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Promise ship
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    purchaseOrder.promiseShipDate
                      ? 'text.primary'
                      : 'text.secondary'
                  }
                >
                  {purchaseOrder.promiseShipDate?.toLocaleString({
                    dateStyle: 'medium',
                  }) || 'Not set'}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* To Location */}
          <Box marginTop={2} paddingX={1}>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="column" spacing={0.5}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="subtitle2">To: </Typography>
                  <Typography variant="body2">
                    {getEffectiveLocationName(purchaseOrder.toLocation)}
                  </Typography>
                  <LocationIntegrationStatus
                    location={purchaseOrder.toLocation}
                    onResolved={invalidateAfterDriftPull}
                  />
                  {isEffectiveThirdPartyEnabled(purchaseOrder.toLocation) && (
                    <Chip label="3PL" size="small" />
                  )}
                </Stack>
                {getEffectiveAddress(purchaseOrder.toLocation) && (
                  <Typography variant="body2" color="text.secondary">
                    {getEffectiveAddress(purchaseOrder.toLocation)}
                  </Typography>
                )}
              </Stack>
              <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Promise delivery
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    purchaseOrder.promiseDeliveryDate
                      ? 'text.primary'
                      : 'text.secondary'
                  }
                >
                  {purchaseOrder.promiseDeliveryDate?.toLocaleString({
                    dateStyle: 'medium',
                  }) || 'Not set'}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Transport ref */}
          {(purchaseOrder.transportReferenceDetails?.carrier ||
            purchaseOrder.transportReferenceDetails?.transportReference) && (
            <Box marginTop={2} paddingX={1}>
              <Divider sx={{ borderStyle: 'dashed', mb: 2 }} />
              <Stack direction="row" justifyContent="flex-end">
                <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Transport ref
                    </Typography>
                    <Tooltip
                      arrow
                      title="Master tracking or carrier reference for this purchase order"
                    >
                      <InfoOutlined
                        sx={{ fontSize: 16, color: 'text.secondary' }}
                      />
                    </Tooltip>
                  </Stack>
                  <TransportRefLabel
                    carrier={
                      purchaseOrder.transportReferenceDetails?.carrier ?? null
                    }
                    carrierName={
                      purchaseOrder.transportReferenceDetails?.name ?? null
                    }
                    trackingNumber={
                      purchaseOrder.transportReferenceDetails
                        ?.transportReference ?? null
                    }
                  />
                </Stack>
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Tabs Section */}
        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            marginBottom={2}
            sx={{ borderBottom: '1px solid #E0E0E0' }}
            marginX={1}
          >
            <HorizontalNavigationLink
              to="/purchase-orders/$purchaseOrderKey"
              params={{ purchaseOrderKey: props.purchaseOrderKey }}
              activeOptions={{ exact: true }}
            >
              Line items
            </HorizontalNavigationLink>
            <HorizontalNavigationLink
              to="/purchase-orders/$purchaseOrderKey/shipments"
              params={{ purchaseOrderKey: props.purchaseOrderKey }}
            >
              Shipments
            </HorizontalNavigationLink>
          </Stack>

          <SupportErrorBoundary message="An error occurred.">
            <Outlet />
          </SupportErrorBoundary>
        </Box>
      </DetailPageBody>

      {/* Edit Purchase Order Dialog */}
      <PurchaseOrderDialog
        isOpen={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        purchaseOrder={purchaseOrder}
      />

      {/* Cancel Purchase Order Dialog */}
      <PurchaseOrderCancelDialog
        isOpen={isCancelDialogOpen}
        onClose={handleCloseCancelDialog}
        purchaseOrderKey={props.purchaseOrderKey}
        poNumber={purchaseOrder.purchaseOrderNumber}
        onSuccess={handleCancelSuccess}
      />

      {/* Send to 3PL Dialog */}
      <PurchaseOrderSendTo3PLDialog
        isOpen={isSendTo3PLDialogOpen}
        onClose={handleCloseSendTo3PLDialog}
        purchaseOrderKey={props.purchaseOrderKey}
        poNumber={purchaseOrder.purchaseOrderNumber}
        onSuccess={handleSendTo3PLSuccess}
      />

      {/* Inbound Shipment Dialog for Receiving */}
      <InboundShipmentDialog
        isOpen={isReceiveDialogOpen}
        onClose={handleCloseReceiveDialog}
        purchaseOrderKey={purchaseOrder.key}
        dialogTitle="Record a shipment"
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: purchaseOrderDetailQueryKey(props.purchaseOrderKey),
          });
        }}
      />

      {/* Clone Purchase Order Dialog */}
      <PurchaseOrderDialog
        isOpen={isCloneDialogOpen}
        onClose={handleCloseCloneDialog}
        initialFormData={cloneFormData}
      />
    </Container>
  );
}
